import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Box } from '@common-fe/common-fe';

import DocumentViewer, { NoFiles } from '@/components/elements/DocumentViewer';
import { RenderWrapperProps } from '@/components/elements/DocumentViewer/DocumentViewer.constants';
import useGetDocumentReceipts from '@/components/elements/DocumentViewer/queries/useGetDocumentFiles.query';
import { LockboxStatus } from '@/modules/employer/components/Lockbox/lockbox.types';
import { useAddImageBlobToGroups } from '@/modules/employer/components/Lockbox/LockboxDetails/hooks';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useLockboxDividingModeStore } from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDividingMode.store';

import DividingModeImageCheckbox from './DividingModeImageCheckbox';

interface Props {
  documentId?: string;
}

const LockboxDocumentPreview: React.FC<Props> = ({ documentId }) => {
  const { renderGroupImage } = useAddImageBlobToGroups();
  const { numberOfPages, setNumberOfPages } = useLockboxDividingModeStore();
  const { isDividingMode, originalLockbox } = useLockboxDetailsStore();
  const { files, isFilesLoading } = useGetDocumentReceipts(documentId || '', false);
  const setIsMultiPageViewModeRef = useRef<((value: boolean) => void) | null>(null);

  const hasFiles = files.length > 0;

  const renderWrapper = useCallback(({ index, document, content, isMultiPageViewMode, setIsMultiPageViewMode }: RenderWrapperProps) => {
    const pageNumber = index + 1;
    if (pageNumber && pageNumber > numberOfPages) {
      setNumberOfPages(pageNumber);
    }

    if (!setIsMultiPageViewModeRef.current && setIsMultiPageViewMode && index == 1) {
      setIsMultiPageViewModeRef.current = setIsMultiPageViewMode;
    }

    if (isDividingMode) {
      return (
        <DividingModeImageCheckbox
          key={index}
          document={document}
          pageNumber={`${index + 1}`}
          isMultiPageViewMode={isMultiPageViewMode}
        >
          {content}
        </DividingModeImageCheckbox>
      );
    }

    if (originalLockbox?.lockboxStatus === LockboxStatus.DIVIDED) {
      return renderGroupImage(index, document, content);
    }

    return content;
  }, [isDividingMode, originalLockbox, numberOfPages, renderGroupImage, setNumberOfPages]);

  useLayoutEffect(() => {
    if (setIsMultiPageViewModeRef.current && isDividingMode) {
      setIsMultiPageViewModeRef.current(true);
    }

    return () => {
      if (setIsMultiPageViewModeRef.current) {
        setIsMultiPageViewModeRef.current(false);
      }
    };
  }, [setIsMultiPageViewModeRef, isDividingMode]);

  return (
    <Box height="100%">
      {isFilesLoading || hasFiles ? (
        <DocumentViewer
          receipts={files}
          isLoading={isFilesLoading}
          renderWrapper={renderWrapper}
        />
      ) : (
        <NoFiles title="There are no files uploaded" />
      )}
    </Box>
  );
};

export default LockboxDocumentPreview;
